/**
 * ChatPage - Chat interface using Simulacra design system
 */
import React, { useState, useRef, useEffect } from 'react';
import { YStack, XStack, H1, H2, Text, Input, ScrollView, Spinner } from 'tamagui';
import { GlassCard, MatrixButton, MessageBubble } from '../components/base';
import TradingViewChart from '../components/TradingViewChart';
import { useTheme } from '../App';

const API_URL = 'https://nings-api.beamer-labs.workers.dev';

interface Message {
  id: string;
  content: string;
  variant: 'user' | 'assistant';
  created_at: string;
  response?: {
    agent?: string;
    content?: string;
    status: 'success' | 'failed';
    retryable?: boolean;
    error?: string;
  };
}

interface FollowedTicker {
  id: string;
  ticker_details: {
    tags: {
      symbol: string;
    };
    details: {
      company_name: string;
      sector: string;
    };
  };
}

interface ChatPageProps {
  accessToken: string | null;
}

const ChatPage: React.FC<ChatPageProps> = ({ accessToken }) => {
  const { theme } = useTheme();
  const [selectedTicker, setSelectedTicker] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [followedTickers, setFollowedTickers] = useState<FollowedTicker[]>([]);
  const [loadingTickers, setLoadingTickers] = useState(true);
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);
  const [showChart, setShowChart] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch followed tickers on mount
  useEffect(() => {
    const fetchFollowedTickers = async () => {
      if (!accessToken) {
        setLoadingTickers(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/ticker_follow/query`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          console.log('=== TICKER API RESPONSE ===');
          console.log('Full response:', data);
          console.log('Tickers array:', data.tickers);
          if (data.tickers && data.tickers[0]) {
            console.log('First ticker:', data.tickers[0]);
            console.log('First ticker_details:', data.tickers[0].ticker_details);
            if (data.tickers[0].ticker_details) {
              console.log('Symbol path:', data.tickers[0].ticker_details?.tags?.symbol);
              console.log('Company path:', data.tickers[0].ticker_details?.details?.company_name);
            }
          }
          setFollowedTickers(data.tickers || []);
        }
      } catch (error) {
        console.error('Failed to fetch followed tickers:', error);
      } finally {
        setLoadingTickers(false);
      }
    };

    fetchFollowedTickers();
  }, [accessToken]);

  // Load message history when ticker is selected
  const loadTickerChat = async (tickerSymbol: string, companyName: string) => {
    setSelectedTicker(tickerSymbol);
    setSelectedCompanyName(companyName);
    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/messages/${tickerSymbol}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Messages API response:', data);
        if (data.success && data.messages) {
          const uiMessages: Message[] = data.messages.map((msg: any) => ({
            id: msg.id,
            content: msg.tags?.content || '',
            variant: msg.variant,
            created_at: msg.created_at,
            response: msg.details?.response
          }));
          setMessages(uiMessages);
        } else if (data.messages) {
          // Handle case where success flag might be missing but messages exist
          const uiMessages: Message[] = data.messages.map((msg: any) => ({
            id: msg.id,
            content: msg.tags?.content || '',
            variant: msg.variant,
            created_at: msg.created_at,
            response: msg.details?.response
          }));
          setMessages(uiMessages);
        }
      } else {
        const errorText = await response.text();
        console.error('Messages API error:', response.status, errorText);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || !selectedTicker) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/api/chat?ticker=${selectedTicker}&content=${encodeURIComponent(userMessage)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.message) {
          const newMessage: Message = {
            id: data.message.id,
            content: data.message.tags?.content || '',
            variant: data.message.variant,
            created_at: data.message.created_at,
            response: data.message.details?.response
          };
          setMessages(prev => [...prev, newMessage]);
        }
      } else {
        const errorData = await response.json();
        if (errorData.detail && errorData.detail.message) {
          const failedMessage: Message = {
            id: errorData.detail.message.id,
            content: errorData.detail.message.tags?.content || '',
            variant: errorData.detail.message.variant,
            created_at: errorData.detail.message.created_at,
            response: errorData.detail.message.details?.response
          };
          setMessages(prev => [...prev, failedMessage]);
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (messageId: string) => {
    setRetryingMessageId(messageId);

    try {
      const response = await fetch(
        `${API_URL}/api/chat/retry/${messageId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => prev.map(msg => {
          if (msg.id === messageId) {
            return {
              ...msg,
              response: {
                agent: 'grok-2-1212',
                content: data.answer,
                status: 'success'
              }
            };
          }
          return msg;
        }));
      }
    } catch (error) {
      console.error('Failed to retry message:', error);
    } finally {
      setRetryingMessageId(null);
    }
  };

  return (
    <XStack flex={1} height="calc(100vh - 64px)" width="100%">
      {/* Sidebar */}
      <YStack
        width={280}
        backgroundColor="$backgroundPress"
        borderRightWidth={1}
        borderRightColor="$borderColor"
        padding="$4"
        gap="$4"
        $sm={{ display: selectedTicker ? 'none' : 'flex', width: '100%' }}
      >
        <YStack gap="$2">
          <H2 fontSize="$5" color="$green10" fontFamily="$heading">
            Followed Tickers
          </H2>
          <Text fontSize="$2" color="$placeholderColor" fontFamily="$body">
            Select a ticker to chat
          </Text>
        </YStack>

        {loadingTickers ? (
          <YStack alignItems="center" paddingVertical="$6">
            <Spinner size="small" color="$green10" />
          </YStack>
        ) : followedTickers.length > 0 ? (
          <YStack gap="$2">
            {followedTickers.map((ft) => {
              console.log('Rendering ticker:', ft.id, 'ticker_details:', ft.ticker_details);
              try {
                const symbol = ft.ticker_details.tags.symbol;
                const companyName = ft.ticker_details.details.company_name;
                console.log('Successfully got symbol:', symbol, 'company:', companyName);
              } catch (error) {
                console.error('Error accessing ticker_details:', error);
                console.log('ft object:', ft);
              }
              return (
              <GlassCard
                key={ft.id}
                glowOnHover
                onPress={() => loadTickerChat(
                  ft.ticker_details?.tags?.symbol,
                  ft.ticker_details?.details?.company_name
                )}
                pressStyle={{ scale: 0.98 }}
                cursor="pointer"
                backgroundColor={selectedTicker === ft.ticker_details?.tags?.symbol ? '$backgroundHover' : '$backgroundPress'}
                borderColor={selectedTicker === ft.ticker_details?.tags?.symbol ? '$green10' : '$borderColor'}
              >
                <YStack gap="$1">
                  <Text fontFamily="$heading" fontSize="$4" fontWeight="bold" color="$green10">
                    #{ft.ticker_details?.tags?.symbol || 'N/A'}
                  </Text>
                  <Text fontFamily="$body" fontSize="$2" color="$colorPress" numberOfLines={1}>
                    {ft.ticker_details?.details?.company_name || 'Unknown'}
                  </Text>
                </YStack>
              </GlassCard>
            )})}
          </YStack>
        ) : (
          <GlassCard>
            <YStack gap="$2">
              <Text fontFamily="$body" fontSize="$3" color="$color">
                No followed tickers yet.
              </Text>
              <Text fontFamily="$body" fontSize="$2" color="$placeholderColor">
                Visit the Discover page to follow tickers.
              </Text>
            </YStack>
          </GlassCard>
        )}
      </YStack>

      {/* Main chat area */}
      <YStack flex={1} $sm={{ display: selectedTicker ? 'flex' : 'none' }}>
        {!selectedTicker ? (
          <YStack flex={1} alignItems="center" justifyContent="center" gap="$4" padding="$6">
            <H1 color="$green10" fontFamily="$heading" textAlign="center">
              Chat with Your Tickers
            </H1>
            <Text color="$colorPress" fontFamily="$body" fontSize="$4" textAlign="center">
              Ask questions as if you're talking to the company itself
            </Text>
            <Text color="$placeholderColor" fontFamily="$body" fontSize="$3" textAlign="center">
              Choose a followed ticker from the sidebar to begin your conversation.
            </Text>
          </YStack>
        ) : (
          <YStack flex={1} height="100%">
            {/* Chat header */}
            <XStack
              padding="$4"
              backgroundColor="$backgroundPress"
              borderBottomWidth={1}
              borderBottomColor="$borderColor"
              justifyContent="space-between"
              alignItems="center"
              gap="$3"
            >
              <YStack flex={1}>
                <Text fontSize="$2" color="$placeholderColor" fontFamily="$mono">
                  Chatting with:
                </Text>
                <XStack gap="$2" alignItems="center">
                  <Text fontSize="$5" fontWeight="bold" color="$green10" fontFamily="$heading">
                    {selectedTicker}
                  </Text>
                  <Text fontSize="$3" color="$colorPress" fontFamily="$body">
                    ({selectedCompanyName})
                  </Text>
                </XStack>
              </YStack>
              <XStack gap="$2">
                <MatrixButton
                  variant={showChart ? 'primary' : 'ghost'}
                  size="$2"
                  onPress={() => setShowChart(!showChart)}
                >
                  {showChart ? 'Hide Chart' : 'Show Chart'}
                </MatrixButton>
                <MatrixButton
                  variant="ghost"
                  size="$2"
                  onPress={() => {
                    setSelectedTicker('');
                    setSelectedCompanyName('');
                    setMessages([]);
                    setShowChart(false);
                  }}
                >
                  Change
                </MatrixButton>
              </XStack>
            </XStack>

            {/* TradingView Chart (collapsible) */}
            {showChart && (
              <YStack
                padding="$4"
                backgroundColor="$background"
                borderBottomWidth={1}
                borderBottomColor="$borderColor"
              >
                <TradingViewChart symbol={selectedTicker} theme={theme} />
              </YStack>
            )}

            {/* Messages */}
            <ScrollView flex={1} padding="$4">
              <YStack gap="$3" paddingBottom="$4">
                {messages.map((msg) => (
                  <YStack key={msg.id} gap="$2">
                    <MessageBubble
                      role={msg.variant}
                      content={msg.content}
                      timestamp={new Date(msg.created_at).toLocaleTimeString()}
                    />

                    {/* AI response */}
                    {msg.response && msg.response.status === 'success' && msg.response.content && (
                      <GlassCard backgroundColor="$backgroundHover" borderColor="$borderPastel">
                        <YStack gap="$2">
                          <Text fontSize="$2" color="$green9" fontFamily="$mono" textTransform="uppercase">
                            {selectedTicker} responds:
                          </Text>
                          <Text fontSize="$3" color="$color" fontFamily="$body">
                            {msg.response.content}
                          </Text>
                        </YStack>
                      </GlassCard>
                    )}

                    {/* Error */}
                    {msg.response && msg.response.status === 'failed' && (
                      <GlassCard backgroundColor="$backgroundPress" borderColor="$red10" borderWidth={2}>
                        <YStack gap="$3">
                          <Text fontSize="$2" color="$red10" fontFamily="$mono">
                            ❌ Error: {msg.response.error}
                          </Text>
                          {msg.response.retryable && (
                            <MatrixButton
                              variant="secondary"
                              size="$2"
                              onPress={() => handleRetry(msg.id)}
                              disabled={retryingMessageId === msg.id}
                            >
                              {retryingMessageId === msg.id ? 'Retrying...' : 'Retry'}
                            </MatrixButton>
                          )}
                        </YStack>
                      </GlassCard>
                    )}
                  </YStack>
                ))}

                {loading && (
                  <YStack alignItems="center" paddingVertical="$4">
                    <Spinner size="large" color="$green10" />
                  </YStack>
                )}
                <div ref={messagesEndRef} />
              </YStack>
            </ScrollView>

            {/* Input form */}
            <XStack
              padding="$4"
              backgroundColor="$backgroundPress"
              borderTopWidth={1}
              borderTopColor="$borderColor"
              gap="$3"
            >
              <form onSubmit={handleSendMessage} style={{ display: 'flex', flex: 1, gap: '12px' }}>
                <Input
                  flex={1}
                  placeholder={`Ask ${selectedTicker} a question...`}
                  value={inputMessage}
                  onChangeText={setInputMessage}
                  disabled={loading}
                  backgroundColor="$backgroundHover"
                  borderColor="$borderColor"
                  color="$color"
                  placeholderTextColor="$placeholderColor"
                  fontFamily="$body"
                />
                <MatrixButton
                  variant="primary"
                  disabled={loading || !inputMessage.trim()}
                  onPress={handleSendMessage}
                >
                  Send
                </MatrixButton>
              </form>
            </XStack>
          </YStack>
        )}
      </YStack>
    </XStack>
  );
};

export default ChatPage;
